import { Box, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import { IWorkflowBaseRecord, IWorkflowNodeConfig } from "../interfaces";
import { RecordCard } from "./RecordCard";

interface IConditionalRelationChangesModalProps {
  node: IWorkflowNodeConfig;
  addedBindingItems: IWorkflowBaseRecord[];
  removedRelationItems: IWorkflowBaseRecord[];
}

export const ConditionalRelationChangesModal = (props: IConditionalRelationChangesModalProps) => {
  const { node, addedBindingItems, removedRelationItems } = props;

  const getRemovedDisplayRecord = (relationRecord: IWorkflowBaseRecord): IWorkflowBaseRecord => {
    if (!node.binding?.key) {
      return relationRecord;
    }

    const recordMap = relationRecord as unknown as Record<string, unknown>;
    const nestedValue = recordMap[node.binding.key];

    if (nestedValue && typeof nestedValue === "object") {
      return nestedValue as IWorkflowBaseRecord;
    }

    return relationRecord;
  };

  return (
    <Stack
      spacing={2}
      sx={{
        height: "100%",
        minHeight: 0,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="h6" fontWeight={700}>
            Pending changes
          </Typography>

          <Chip label={`Added: ${addedBindingItems.length}`} size="small" color="success" variant="outlined" />
          <Chip label={`Removed: ${removedRelationItems.length}`} size="small" color="error" variant="outlined" />
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Review the items that will be added to or removed from the current relation before saving.
        </Typography>
      </Paper>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          overflow: "hidden",
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 3,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: "success.main" }}>
            Added items
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {addedBindingItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No items will be added.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {addedBindingItems.map((item) => (
                  <RecordCard
                    key={item.uid}
                    title={item.name ?? item.uid}
                    subtitle={item.description}
                    imageUrl={item.imageUrl}
                    record={item}
                    fields={[]}
                    selected={false}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 3,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: "error.main" }}>
            Removed items
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {removedRelationItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No items will be removed.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {removedRelationItems.map((relationRecord) => {
                  const displayRecord = getRemovedDisplayRecord(relationRecord);

                  return (
                    <RecordCard
                      key={relationRecord.uid}
                      title={displayRecord.name ?? relationRecord.uid}
                      subtitle={displayRecord.description}
                      imageUrl={displayRecord.imageUrl}
                      record={displayRecord}
                      fields={[]}
                      selected={false}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>
    </Stack>
  );
};
